/**
 * useGetSectionSublabels — live second lines for rail items whose section is
 * a gamification engagement.
 *
 * A rail sublabel like "Ready now" or "6 days" is STATE, not authored copy —
 * writing it into config makes it wrong within a day. This hook fetches the
 * referenced gamification models (participation rides the same response for
 * a signed-in member) and derives the line per TYPE through a declarative
 * formatter map — the same doctrine as the slot tables: a new placement is a
 * row, never a branch in a renderer, and no campaign's words appear here.
 *
 * A type with no row, a member with no state, or a signed-out viewer derives
 * nothing — the item falls back to its authored static sublabel (or none).
 * Inventing "0 days" would put a number on the page that means nothing.
 */
import {useQueries} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import {GamificationApi, type GamificationModelResponse, type GamificationStreakBoardUserState, type GamificationWheelSpinState} from "@devour/client";
import {GAMIFICATION_MODEL_QUERY_KEY} from "./useGetGamificationModel";

/** The slice of a section this reads. Structural — no host vocabulary. */
export interface SublabelSection {
    _id?: string;
    id?: string;
    engagementType?: string;
    engagementId?: string;
}

/**
 * `GamificationType` -> the derived line. DATA, not code: each entry reads
 * the model + the member's participation and returns a line or nothing.
 * Formats deliberately mirror the design corpus's own vocabulary ("Ready
 * now", "6 days", "Open until Oct 8") without ever inventing a state the
 * data doesn't show.
 */
const SUBLABEL_FOR: Partial<Record<string, (model: GamificationModelResponse) => string | undefined>> = {
    WHEEL_SPIN: model => {
        const userState: Partial<GamificationWheelSpinState> | undefined = model.participation?.userState;
        const spins = userState?.spinsRemaining;
        return typeof spins === "number" && spins > 0 ? "Ready now" : undefined;
    },
    STREAK_BOARD: model => {
        const userState: Partial<GamificationStreakBoardUserState> | undefined = model.participation?.userState;
        const streak = userState?.currentStreak;
        if (typeof streak !== "number" || streak < 1) return undefined;
        return streak === 1 ? "1 day" : `${streak} days`;
    },
    PREDICTIONS: model => {
        if (!model.endAt || model.endAt <= Date.now()) return undefined;
        const closes = new Date(model.endAt).toLocaleDateString(undefined, {month: "short", day: "numeric"});
        return `Open until ${closes}`;
    },
};

/**
 * Section id -> derived sublabel, for every GAMIFICATION section whose model
 * resolves and whose type has a formatter row. Keys are SECTION ids (what
 * `railItems` addresses), not model ids.
 */
export function useGetSectionSublabels(sections: ReadonlyArray<SublabelSection> | undefined): Record<string, string> {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const gamificationSections = (sections ?? []).filter(s => s.engagementType === "GAMIFICATION" && s.engagementId);

    const results = useQueries({
        queries: gamificationSections.map(s => ({
            queryKey: [GAMIFICATION_MODEL_QUERY_KEY, s.engagementId, Boolean(fullToken?.token)],
            queryFn: () => new GamificationApi(getConfig(fullToken)).getGamificationModelsId({id: s.engagementId!}),
            /*
             * Same staleness posture as the participation hook: out-of-band
             * grants (milestone spins) surface within one poll.
             */
            refetchOnWindowFocus: true,
            refetchInterval: 30000,
        })),
    });

    const sublabels: Record<string, string> = {};
    gamificationSections.forEach((s, i) => {
        const model = results[i]?.data;
        if (!model) return;
        const line = SUBLABEL_FOR[model.type]?.(model);
        const sectionId = s._id ?? s.id;
        if (line && sectionId) sublabels[sectionId] = line;
    });
    return sublabels;
}
